// CallOverlay — global pop-up showing the active call OR an incoming
// ring. Renders on top of every page so a call experience works no
// matter where the user is.
import React, { useEffect, useRef, useState } from "react";
import { Phone, PhoneSlash, VideoCamera, MicrophoneSlash, Microphone, VideoCameraSlash, ArrowsClockwise } from "@phosphor-icons/react";
import { useLiveCalls } from "../context/LiveCallsContext";

function VideoTile({ stream, label, muted = false }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && stream) {
      ref.current.srcObject = stream;
    }
  }, [stream]);
  return (
    <div className="relative rounded-2xl overflow-hidden bg-ink/70 aspect-video">
      <video ref={ref} autoPlay playsInline muted={muted} className="w-full h-full object-cover" />
      <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded-full bg-ink/60 text-cream text-[10px] tracking-wider uppercase">
        {label}
      </div>
    </div>
  );
}

// Format seconds → "M:SS" / "H:MM:SS" — used for the live call clock.
function fmtElapsed(sec) {
  if (sec < 0 || !Number.isFinite(sec)) return "0:00";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  if (h) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function CallOverlay() {
  const { callState, localStream, remoteStreams, acceptCall, declineCall, hangup } = useLiveCalls();
  const [muted, setMuted] = useState(false);
  const [videoOff, setVideoOff] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const connectedAtRef = useRef(null);

  // Mute / video toggle by directly twiddling the MediaStreamTracks
  // so we don't have to renegotiate the WebRTC connection.
  useEffect(() => {
    if (!localStream) return;
    localStream.getAudioTracks().forEach((t) => { t.enabled = !muted; });
    localStream.getVideoTracks().forEach((t) => { t.enabled = !videoOff; });
  }, [localStream, muted, videoOff]);

  // Live duration: tick once a second as soon as we have at least one
  // remote stream (i.e. the other side actually joined). Reset the
  // clock when the call ends.
  useEffect(() => {
    const hasPeer = Object.keys(remoteStreams).length > 0;
    if (!hasPeer || callState.phase === "idle") {
      connectedAtRef.current = null;
      setElapsed(0);
      return;
    }
    if (connectedAtRef.current == null) connectedAtRef.current = Date.now();
    const tick = () => setElapsed(Math.floor((Date.now() - connectedAtRef.current) / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [remoteStreams, callState.phase]);

  // Switch between front/back camera on phones. We grab a fresh
  // stream with the opposite facingMode, then swap the video track
  // into every active RTCRtpSender so peers see the new feed.
  const flipCamera = async () => {
    if (!localStream) return;
    try {
      const oldTrack = localStream.getVideoTracks()[0];
      const facing = oldTrack?.getSettings?.().facingMode === "user" ? "environment" : "user";
      const fresh = await navigator.mediaDevices.getUserMedia({ video: { facingMode: facing }, audio: false });
      const newTrack = fresh.getVideoTracks()[0];
      if (!newTrack) return;
      // Hot-swap the track on every peer connection sender.
      window.__hwLiveCallsPeers?.forEach?.((pc) => {
        const sender = pc.getSenders().find((s) => s.track && s.track.kind === "video");
        sender?.replaceTrack(newTrack).catch(() => {});
      });
      // Update the local MediaStream so the self-view re-renders.
      localStream.removeTrack(oldTrack); oldTrack.stop();
      localStream.addTrack(newTrack);
    } catch (_) { /* user denied or one-camera device */ }
  };

  // Incoming call ring — full-screen accept/decline.
  if (callState.incoming) {
    const inv = callState.incoming;
    return (
      <div className="fixed inset-0 z-[200] bg-ink/95 grid place-items-center p-6" data-testid="incoming-call-screen">
        <div className="text-center text-cream">
          <p className="text-[10px] tracking-[0.3em] uppercase opacity-70 mb-2">{inv.kind === "group" ? "Group call" : "Incoming call"}</p>
          <h2 className="font-heading text-5xl">{inv.from_name}</h2>
          {inv.title && <p className="mt-2 opacity-80">{inv.title}</p>}
          <div className="mt-12 flex items-center justify-center gap-10">
            <button
              onClick={() => declineCall(inv.call_id)}
              data-testid="incoming-call-decline"
              className="w-16 h-16 rounded-full bg-terracotta hover:bg-terracotta-600 grid place-items-center"
              aria-label="Decline"
            >
              <PhoneSlash size={26} weight="fill" />
            </button>
            <button
              onClick={() => acceptCall(inv.call_id)}
              data-testid="incoming-call-accept"
              className="w-16 h-16 rounded-full bg-forest hover:bg-forest-600 grid place-items-center animate-pulse"
              aria-label="Accept"
            >
              <Phone size={26} weight="fill" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (callState.phase === "idle" || !callState.callId) return null;

  // Active call — show local + remote tiles + controls.
  const otherStreams = Object.entries(remoteStreams);
  return (
    <div className="fixed inset-0 z-[150] bg-ink/95 flex flex-col" data-testid="active-call-screen">
      <div className="p-4 flex items-center justify-between text-cream">
        <span className="text-[10px] tracking-[0.3em] uppercase opacity-70">
          {callState.kind === "group" ? "Group call" : "Call"} · {callState.phase}
        </span>
        <div className="flex items-center gap-3">
          {callState.connectionWarning && (
            <span
              data-testid="call-reconnecting"
              className="text-[10px] tracking-[0.18em] uppercase font-bold bg-amber-500 text-ink px-2.5 py-1 rounded-full animate-pulse"
            >
              {callState.connectionWarning}
            </span>
          )}
          {elapsed > 0 && (
            <span data-testid="call-duration" className="text-xs font-mono tabular-nums bg-cream/15 px-2.5 py-1 rounded-full">
              {fmtElapsed(elapsed)}
            </span>
          )}
          {callState.title && <span className="text-sm opacity-80">{callState.title}</span>}
        </div>
      </div>
      <div className="flex-1 grid gap-3 p-4 overflow-auto" style={{ gridTemplateColumns: `repeat(${Math.min(2, Math.max(1, otherStreams.length))}, minmax(0, 1fr))` }}>
        {otherStreams.length === 0 ? (
          <div className="grid place-items-center text-cream/70 col-span-2">
            <div className="text-center">
              <Phone size={48} weight="duotone" className="mx-auto opacity-60" />
              <p className="mt-3">{callState.phase === "ringing" ? "Ringing…" : "Connecting…"}</p>
            </div>
          </div>
        ) : (
          otherStreams.map(([uid, stream]) => (
            <VideoTile key={uid} stream={stream} label="Friend" />
          ))
        )}
      </div>
      {/* Self-view, picture-in-picture style */}
      <div className="absolute top-16 right-4 w-32 sm:w-44 rounded-xl overflow-hidden border-2 border-cream/20 shadow-2xl">
        {localStream && <VideoTile stream={localStream} label="You" muted />}
      </div>
      <div className="p-6 flex items-center justify-center gap-4 bg-ink">
        <button
          onClick={() => setMuted((v) => !v)}
          data-testid="call-mute"
          className={`w-14 h-14 rounded-full grid place-items-center ${muted ? "bg-amber-500 text-ink" : "bg-cream/15 text-cream hover:bg-cream/25"}`}
          aria-label="Toggle mic"
        >
          {muted ? <MicrophoneSlash size={22} weight="fill" /> : <Microphone size={22} weight="fill" />}
        </button>
        <button
          onClick={() => setVideoOff((v) => !v)}
          data-testid="call-video"
          className={`w-14 h-14 rounded-full grid place-items-center ${videoOff ? "bg-amber-500 text-ink" : "bg-cream/15 text-cream hover:bg-cream/25"}`}
          aria-label="Toggle camera"
        >
          {videoOff ? <VideoCameraSlash size={22} weight="fill" /> : <VideoCamera size={22} weight="fill" />}
        </button>
        <button
          onClick={flipCamera}
          data-testid="call-flip-camera"
          className="w-14 h-14 rounded-full bg-cream/15 hover:bg-cream/25 text-cream grid place-items-center"
          aria-label="Flip camera"
          title="Switch front/back camera"
        >
          <ArrowsClockwise size={22} weight="bold" />
        </button>
        <button
          onClick={hangup}
          data-testid="call-hangup"
          className="w-14 h-14 rounded-full bg-terracotta hover:bg-terracotta-600 grid place-items-center text-cream"
          aria-label="Hang up"
        >
          <PhoneSlash size={22} weight="fill" />
        </button>
      </div>
    </div>
  );
}
